/**
 * Hook pour la gestion du contenu multilingue
 *
 * Fournit les utilitaires pour gérer l'affichage et la saisie de contenu
 * dans plusieurs langues selon la configuration de l'entreprise.
 *
 * @module useMultiLanguage
 */

import {useMemo, useCallback} from 'react';
import {useActiveCompany} from './session';
import {getAllLanguages} from 'packages/common/locales/languageConfig';

/**
 * Configuration des langues disponibles dans le système
 * Récupérée depuis la source unique de vérité
 * @constant {Array<Object>}
 */
const AVAILABLE_LANGUAGES = getAllLanguages();

/**
 * Hook principal pour la gestion multilingue
 *
 * @return {Object} Utilitaires et informations multilingues
 */
export const useMultiLanguage = () => {
  const {company} = useActiveCompany();

  // Extraction de la configuration pour éviter l'expression complexe dans les dépendances
  const companyMultiLanguageConfig = company?.multiLanguage;

  // Configuration multilingue de l'entreprise
  const multiLanguageConfig = useMemo(() => {
    return {
      enabled: companyMultiLanguageConfig?.enabled || false,
      defaultLanguage: companyMultiLanguageConfig?.defaultLanguage || 'fr',
      enabledLanguages: companyMultiLanguageConfig?.enabledLanguages || ['fr'],
    };
  }, [companyMultiLanguageConfig]);

  // Langues disponibles filtrées selon la configuration de l'entreprise
  const availableLanguages = useMemo(() => {
    return AVAILABLE_LANGUAGES.filter((lang) =>
      multiLanguageConfig.enabledLanguages.includes(lang.code),
    );
  }, [multiLanguageConfig.enabledLanguages]);

  /**
   * Récupère le contenu localisé avec fallback automatique
   *
   * @param {Object} content - Contenu avec traductions
   * @param {string} field - Nom du champ à récupérer
   * @param {string} [language] - Langue demandée (par défaut: langue par défaut)
   * @returns {string} Contenu localisé
   */
  const getLocalizedContent = useCallback((content, field, language = multiLanguageConfig.defaultLanguage) => {
    if (!content) return '';

    // Si multilingue désactivé, retourne toujours le champ original
    if (!multiLanguageConfig.enabled) {
      return content[field] || '';
    }

    // Logique de fallback : langue demandée -> langue par défaut -> champ original -> vide
    return (
      content.translations?.[field]?.[language] ||
      content.translations?.[field]?.[multiLanguageConfig.defaultLanguage] ||
      content[field] ||
      ''
    );
  }, [multiLanguageConfig.enabled, multiLanguageConfig.defaultLanguage]);

  /**
   * Prépare les données de formulaire pour la soumission
   *
   * Assure que la langue par défaut est aussi dans le champ principal
   * pour maintenir la compatibilité descendante.
   *
   * @param {Object} formData - Données du formulaire
   * @param {string[]} translatableFields - Champs traduisibles
   * @returns {Object} Données formatées pour l'API
   */
  const prepareFormData = useCallback((formData, translatableFields = []) => {
    if (!multiLanguageConfig.enabled || !formData.translations) {
      return formData;
    }

    const preparedData = {...formData};

    // Met à jour les champs principaux avec la valeur de la langue par défaut
    translatableFields.forEach((field) => {
      const defaultValue = formData.translations?.[field]?.[multiLanguageConfig.defaultLanguage];
      if (defaultValue) {
        preparedData[field] = defaultValue;
      }
    });

    return preparedData;
  }, [multiLanguageConfig.enabled, multiLanguageConfig.defaultLanguage]);

  /**
   * Initialise les données de traduction pour un formulaire
   *
   * @param {Object} existingData - Données existantes (pour édition)
   * @param {string[]} translatableFields - Champs traduisibles
   * @returns {Object} Données initialisées avec structure de traductions
   */
  const initializeFormData = useCallback((existingData = {}, translatableFields = []) => {
    if (!multiLanguageConfig.enabled) {
      return existingData;
    }

    const initializedData = {...existingData};

    // Initialise la structure de traductions si elle n'existe pas
    if (!initializedData.translations) {
      initializedData.translations = {};
    }

    // Initialise chaque champ traduisible pour TOUTES les langues activées
    translatableFields.forEach((field) => {
      if (!initializedData.translations[field]) {
        initializedData.translations[field] = {};
      }

      // Initialise tous les champs de langues avec des chaînes vides
      multiLanguageConfig.enabledLanguages.forEach((langCode) => {
        if (!initializedData.translations[field][langCode]) {
          // Si c'est la langue par défaut et qu'on a une valeur dans le champ principal
          if (langCode === multiLanguageConfig.defaultLanguage && existingData[field]) {
            initializedData.translations[field][langCode] = existingData[field];
          } else if (existingData.translations?.[field]?.[langCode]) {
            // Si on a déjà une traduction pour cette langue
            initializedData.translations[field][langCode] = existingData.translations[field][langCode];
          } else {
            // Sinon, initialise avec une chaîne vide
            initializedData.translations[field][langCode] = '';
          }
        }
      });
    });

    return initializedData;
  }, [multiLanguageConfig.enabled, multiLanguageConfig.defaultLanguage, multiLanguageConfig.enabledLanguages]);

  /**
   * Vérifie si un champ a des traductions dans d'autres langues
   *
   * @param {Object} content - Contenu à vérifier
   * @param {string} field - Nom du champ
   * @returns {boolean} True si des traductions existent
   */
  const hasTranslations = useCallback((content, field) => {
    if (!multiLanguageConfig.enabled || !content?.translations?.[field]) {
      return false;
    }

    return multiLanguageConfig.enabledLanguages.some((lang) =>
      lang !== multiLanguageConfig.defaultLanguage &&
      content.translations[field][lang],
    );
  }, [multiLanguageConfig.enabled, multiLanguageConfig.enabledLanguages, multiLanguageConfig.defaultLanguage]);

  return {
    // Configuration
    isEnabled: multiLanguageConfig.enabled,
    defaultLanguage: multiLanguageConfig.defaultLanguage,
    enabledLanguages: multiLanguageConfig.enabledLanguages,
    availableLanguages,

    // Utilitaires
    getLocalizedContent,
    prepareFormData,
    initializeFormData,
    hasTranslations,

    // Constantes
    ALL_LANGUAGES: AVAILABLE_LANGUAGES,
  };
};

export default useMultiLanguage;
